// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import DashboardForm, { DashboardFormValues } from '@Organisms/Dashboard/DashboardDrawer/DashboardForm';
import { DashboardOutput, UserOutput } from 'thehive-sdk';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import useValidateForm from '@Hooks/useValidateForm';
import { useLocalMessage } from '@Helpers/locale';
import { useCurrentUser } from '@Queries/userQueries';
import useListQuery from '@Hooks/useListQuery';
import client from '@Client';
import { computeListWithoutServiceSelector as computeNormalUserSelector } from '@Organisms/User/selector';
import { computeListSelector } from '@Organisms/Dashboard/selector';

interface DashboardManageProps {
    initialValues?: DashboardOutput;
    isEditing?: boolean;
    triggerElem: ReactElement;
    onFinish?(values: DashboardFormValues): void;
    submitTouchedValues?: boolean;
}

export const DashboardManage = ({
    initialValues,
    isEditing,
    triggerElem,
    onFinish,
    submitTouchedValues,
}: DashboardManageProps): ReactElement => {
    const i = useLocalMessage();
    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();
    const { data: currentUser } = useCurrentUser();
    const { login } = currentUser || {};
    const orgId = currentUser?.organisation || '';

    const { data: ownerList = [], refetch: refetchOwnerList } = useListQuery<UserOutput[]>({
        queryParams: {
            selector: computeNormalUserSelector({ orgId }),
        },
        controller: client.UserController,
        manual: true,
        fetchOnStart: true,
    });

    const { data: allDashboardList = [], refetch: refetchDashboardList } = useListQuery<DashboardOutput[]>({
        controller: client.DashboardController,
        queryParams: {
            selector: computeListSelector(),
        },
        manual: true,
        fetchOnStart: true,
    });

    return (
        <THDrawer
            title={i(isEditing ? 'dashboard.edit.title' : 'dashboard.create.title')}
            triggerElem={triggerElem}
            className="drawer-form"
            disableClose={fieldsTouched}
            afterVisibleChange={(visible) => {
                form.resetFields();
                if (visible) {
                    refetchDashboardList();
                    if (orgId) {
                        refetchOwnerList();
                    }
                }
            }}
        >
            {(toggleDrawer) => (
                <DashboardForm
                    onCancel={() => {
                        form.resetFields();
                        setFieldsTouched(false);
                        toggleDrawer();
                    }}
                    isValid={isValid && fieldsTouched}
                    onFinish={(values) => {
                        onFinish?.(values);
                        toggleDrawer();
                        setFieldsTouched(false);
                    }}
                    form={form}
                    initialValues={initialValues}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    isUserOwner={initialValues ? login === initialValues?.owner : true}
                    submitTouchedValues={submitTouchedValues}
                    ownerList={ownerList}
                    allDashboardList={allDashboardList}
                    isEditing={isEditing}
                />
            )}
        </THDrawer>
    );
};
